import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit, Output,
  SimpleChanges,
} from '@angular/core';
import {
  NetscopeApiService,
  Success,
  Response
} from "@app/services/netscope-api.service";
import {ClrDatagridSortOrder, ClrDatagridStateInterface} from "@clr/angular";
import {AsnEntry, CountryEntry, FlowDetail, TemporalParameter, DomainResolution} from "@app/netscope/model/data-models";
import {NetscopeUtils} from "@app/netscope/netscope-utils";
import {FlowsRepository} from "@app/netscope/model/flows-repository";

export class NetscopeResource {
    uuid: string;
    name: string;
    type: string;
}

export class ResourceWithSide {
    resource: NetscopeResource;
    side: string
}

export class SharedFilterOptions {
    filterIsActive: boolean = false;
    selectedResources: ResourceWithSide[] = []
}

@Component({
    standalone: false,
    selector: 'app-netscope-flows-datagrid',
    templateUrl: './netscope-flows-datagrid.component.html',
    styleUrls: ['./netscope-flows-datagrid.component.css']
})
export class NetscopeFlowsDatagridComponent implements OnInit, OnChanges, OnDestroy {

    descSort = ClrDatagridSortOrder.DESC;
    ascSort = ClrDatagridSortOrder.ASC;
    unsortedSort = ClrDatagridSortOrder.UNSORTED;

    @Input()
    temporalParameter: TemporalParameter;
    @Input()
    mergeSourceAndTarget: boolean = false;
    @Input()
    bidirectional = false;
    @Input()
    exportCsvEnabled = false;
    @Input()
    callExportCsv = () => {this.defaultCsvExporter(false);};
    @Input()
    callExportCsvWithPortsDetails = () => {this.defaultCsvExporter(true);}

    @Input('thirdPartyRefresh')
    thirdPartyRefresh: (e: ClrDatagridStateInterface) => void = (e) => {};

    @Input()
    disableResolve = false;

    @Input()
    flowDetails: FlowDetail[];

    @Input()
    flowsRepository: FlowsRepository;

    @Input()
    totalItemsCount = 0;

    sharedOptions: SharedFilterOptions = {
        filterIsActive: false,
        selectedResources: []
    }

    @Input()
    showActions = true;

    @Input()
    showHosts = false;

    @Input()
    perPortRow = false;

    @Input()
    showPortInline = false;

    // @Input()
    // displayLinksToRessources = false;

    @Input()
    hidePacketsExchanged = false;

    @Input()
    hideTotalBytes = false;

    @Input()
    showCountry = false;

    @Input()
    showASN = false;

    @Input()
    expandable = true;

    @Input()
    ignoreFilters = false;

    @Input()
    showThreats: boolean = false;

    @Input()
    switchFocus: (FlowRessourceObject) => void = (ressource) => {};

    @Input()
    isFocusedRessource: (FlowRessourceObject) => boolean = (ressource) => false;

    @Input()
    updateEmitter: EventEmitter<FlowDetail[]> = new EventEmitter();

    // The following properties enable advanced filtering of rows of the table
    categories: String[] = [];
    tags: String[] = [];
    protocols: String[] = [];
    insideOutside: number = 0;

    chartCheckInterval;

    shouldUpdateFilters: EventEmitter < any > = new EventEmitter();

    constructor(private netscopeService: NetscopeApiService, private changeDetectorRef: ChangeDetectorRef) {}

    ngOnInit(): void {
      // TODO: the following is to ensure that the datagrid is updated when it is modified by a third party component.
      //  It would be nice to be able to delete this block of code.

      let repositoryUpdateSubscribed = false;
      this.chartCheckInterval = setInterval(() => {
        if (! repositoryUpdateSubscribed && this.flowsRepository !== undefined) {
          this.flowsRepository.repositoryHasBeenUpdated.subscribe(() => {
            this.shouldUpdateFilters.emit();
          });
          repositoryUpdateSubscribed = true;

          setTimeout(() => {
            this.flowsRepository.repositoryHasBeenUpdated.emit();
          });
        }
      }, 50);
    }

    ngOnDestroy(): void {
      if (this.chartCheckInterval) {
        clearInterval(this.chartCheckInterval);
      }
    }

    ngOnChanges(changes: SimpleChanges) {
      // this.shouldUpdateFilters.emit(this.flowDetails);
    }

    defaultCsvExporter = async (withPortsDetails: boolean) => {
        let csvHeader = withPortsDetails ? FlowDetail.defaultCsvHeaderWithPorts() : FlowDetail.defaultCsvHeader();
        let csvContent = csvHeader + "\n";

        let flowDetails = await this.flowsRepository.getAllFlowsForCsvExport(withPortsDetails);

        // for (let flowDetail of this.flowsRepository.get)


        for (let flowDetail of flowDetails) {
          let lineValue = withPortsDetails ? `${flowDetail.exportAsCsvRowWithPorts()}` : `${flowDetail.exportAsCsvRow()}`;
          csvContent += lineValue;
        }

        let exportedFilename = withPortsDetails ? 'dcnetscope-export-with-ports.csv' : 'dcnetscope-export.csv';
        let blob = new Blob([csvContent], {
          type: 'text/csv;charset=utf-8;'
        });
        // @ts-ignore
        if (navigator.msSaveBlob) { // IE 10+
          // @ts-ignore
          navigator.msSaveBlob(blob, exportedFilename);
        } else {
          let link = document.createElement("a");
          if (link.download !== undefined) { // feature detection
            // Browsers that support HTML5 download attribute
            let url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", exportedFilename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        }
    }

    resolveDomain = (flowDetail: FlowDetail, srcOrDst: number) => {
        let ip = srcOrDst === 0 ? flowDetail.src_ipaddr : flowDetail.dst_ipaddr;
        this.netscopeService.getDomainResolution(ip).subscribe((domainResultResponse: Response<DomainResolution>) => {

            let domainResult;
            if (domainResultResponse instanceof Success) {
                domainResult = domainResultResponse.element;
            }

            if (srcOrDst === 0) {
                flowDetail.source.resolvedDomain = domainResult;
            } else {
                flowDetail.target.resolvedDomain = domainResult;
            }
        })
    }

    countrySelector = (countryInfo: CountryEntry) => {
      if (countryInfo == undefined) {
        return "Unknown";
      }
      if (countryInfo.country == undefined) {
        return "this infrastructure";
      }
      return countryInfo.country;
    }

    asnSelector = (asnInfo: AsnEntry) => {
      if (asnInfo == undefined) {
        return "Unknown";
      }
      if (asnInfo?.asn == undefined) {
        return "this infrastructure";
      }
      return asnInfo?.asn;
    }

    sourceCountrySelector = (flowDetail: FlowDetail) => this.countrySelector(flowDetail.source.countryInfo);
    targetCountrySelector = (flowDetail: FlowDetail) => this.countrySelector(flowDetail.target.countryInfo);

    sourceNetworkSelector = (flowDetail: FlowDetail) => this.asnSelector(flowDetail.source.asnInfo);
    targetNetworkSelector = (flowDetail: FlowDetail) => this.asnSelector(flowDetail.source.asnInfo);

    tagsSelector = (flowDetail: FlowDetail) => flowDetail.threat.tags;
    categoriesSelector = (flowDetail: FlowDetail) => flowDetail.threat.categories;
    threatOriginSelector = (flowDetail: FlowDetail) => flowDetail.threat.origin;

    protected readonly NetscopeUtils = NetscopeUtils;

    refresh(state: ClrDatagridStateInterface) {
      if (this.thirdPartyRefresh != undefined) {
        this.thirdPartyRefresh(state);
      }
    }
}

